import {
    projectPhaseSearchType,
    projectSearchSortType,
    projectCatalogCategories,
} from '@/Models';
import {
    projectExplorationTagKeys,
    projectCatalogCategoriesTechAndInnovation,
    projectCatalogCategoriesCreativeWorks,
    projectCatalogCategoriesCommunityProjects,
    projectCatalogCategoriesGamingCulture,
} from './HomeUtilityConstants';

const commonPhaseTypes = {
    upcoming: [projectPhaseSearchType[projectPhaseSearchType.upcoming].name],
    crowdfunding: [projectPhaseSearchType[projectPhaseSearchType.crowdfunding].name],
    latePledge: [projectPhaseSearchType[projectPhaseSearchType.latePledge].name],
    preorder: [projectPhaseSearchType[projectPhaseSearchType.preorder].name],
    active: [
        projectPhaseSearchType[projectPhaseSearchType.crowdfunding].name,
        projectPhaseSearchType[projectPhaseSearchType.latePledge].name,
    ],
    all: [
        projectPhaseSearchType[projectPhaseSearchType.upcoming].name,
        projectPhaseSearchType[projectPhaseSearchType.crowdfunding].name,
        projectPhaseSearchType[projectPhaseSearchType.latePledge].name,
        projectPhaseSearchType[projectPhaseSearchType.preorder].name,
    ],
    allTime: [
        projectPhaseSearchType[projectPhaseSearchType.crowdfunding].name,
        projectPhaseSearchType[projectPhaseSearchType.latePledge].name,
        projectPhaseSearchType[projectPhaseSearchType.recentlyFunded].name,
    ],
};

const commonSortTypes = {
    default: projectSearchSortType[projectSearchSortType.default].name,
    newest: projectSearchSortType[projectSearchSortType.newest].name,
    mostDiscussed: projectSearchSortType[projectSearchSortType.mostDiscussed].name,
    recentUpdates: projectSearchSortType[projectSearchSortType.recentUpdates].name,
    mostFunded: projectSearchSortType[projectSearchSortType.mostFunded].name,
    endingSoon: projectSearchSortType[projectSearchSortType.endingSoon].name,
    mostPopular: projectSearchSortType[projectSearchSortType.mostPopular].name,
};

function buildPhaseSearch(phase, sortType = null) {
    const params = { projectPhaseSearchTypes: phase };
    if (sortType) {
        params.sortType = sortType;
    }
    return params;
}

function buildCategorySearch(categories, phases = null, sortType = commonSortTypes.default) {
    const params = { projectCatalogCategories: categories, sortType };
    if (phases) {
        params.projectPhaseSearchTypes = phases;
    }
    return params;
}

export const projectExplorerSearchParamsValues = {
    // Recommendations
    [projectExplorationTagKeys.exploreYouMayLike]: {},
    [projectExplorationTagKeys.exploreActive]: buildPhaseSearch(commonPhaseTypes.active, null),
    [projectExplorationTagKeys.exploreEndingSoon]: buildPhaseSearch(commonPhaseTypes.active, commonSortTypes.endingSoon),
    [projectExplorationTagKeys.crowdfunding]: buildPhaseSearch(commonPhaseTypes.crowdfunding, null),

    // Highest Funded
    [projectExplorationTagKeys.highestFundedActive]: buildPhaseSearch(commonPhaseTypes.active, commonSortTypes.mostFunded),
    [projectExplorationTagKeys.crowdfundingMostFunded]: buildPhaseSearch(commonPhaseTypes.crowdfunding, commonSortTypes.mostFunded),
    [projectExplorationTagKeys.latePledgeMostFunded]: buildPhaseSearch(commonPhaseTypes.latePledge, commonSortTypes.mostFunded),
    [projectExplorationTagKeys.highestFundedAllTime]: {
        ...buildPhaseSearch(commonPhaseTypes.allTime, commonSortTypes.mostFunded),
        includeClosedProjects: true,
    },

    // Most Discussed
    [projectExplorationTagKeys.mostDiscussedAll]: buildPhaseSearch(commonPhaseTypes.all, commonSortTypes.mostDiscussed),
    [projectExplorationTagKeys.mostDiscussedUpcoming]: buildPhaseSearch(commonPhaseTypes.upcoming, commonSortTypes.mostDiscussed),
    [projectExplorationTagKeys.mostDiscussedCrowdfunding]: buildPhaseSearch(commonPhaseTypes.crowdfunding, commonSortTypes.mostDiscussed),
    [projectExplorationTagKeys.mostDiscussedLatePledge]: buildPhaseSearch(commonPhaseTypes.latePledge, commonSortTypes.mostDiscussed),

    // Recently Added Updates
    [projectExplorationTagKeys.recentlyAddedUpdatesAll]: buildPhaseSearch(commonPhaseTypes.all, commonSortTypes.recentUpdates),
    [projectExplorationTagKeys.recentlyAddedUpdatesUpcoming]: buildPhaseSearch(commonPhaseTypes.upcoming, commonSortTypes.recentUpdates),
    [projectExplorationTagKeys.recentlyAddedUpdatesCrowdfunding]: buildPhaseSearch(commonPhaseTypes.crowdfunding, commonSortTypes.recentUpdates),
    [projectExplorationTagKeys.recentlyAddedUpdatesLatePledge]: buildPhaseSearch(commonPhaseTypes.latePledge, commonSortTypes.recentUpdates),

    // Other phase searches
    [projectExplorationTagKeys.preorders]: buildPhaseSearch(commonPhaseTypes.preorder),
    [projectExplorationTagKeys.majorCreators]: { projectTags: ['majorCreators'] },
    [projectExplorationTagKeys.crowdfundingTrending]: buildPhaseSearch(commonPhaseTypes.crowdfunding),
    [projectExplorationTagKeys.upcomingComingSoon]: buildPhaseSearch(commonPhaseTypes.upcoming),
    [projectExplorationTagKeys.latePledgeNew]: buildPhaseSearch(commonPhaseTypes.latePledge),
    [projectExplorationTagKeys.latePledgeEndingSoon]: buildPhaseSearch(commonPhaseTypes.latePledge, commonSortTypes.endingSoon),
    [projectExplorationTagKeys.crowdfundingEndingSoon]: buildPhaseSearch(commonPhaseTypes.crowdfunding, commonSortTypes.endingSoon),

    // Newest projects
    [projectExplorationTagKeys.crowdfundingNew]: buildPhaseSearch(commonPhaseTypes.crowdfunding, commonSortTypes.newest),
    [projectExplorationTagKeys.upcomingNew]: buildPhaseSearch(commonPhaseTypes.upcoming, commonSortTypes.newest),

    // Most Popular
    [projectExplorationTagKeys.upcomingMostPopular]: buildPhaseSearch(commonPhaseTypes.upcoming, commonSortTypes.mostPopular),

    // Tech And Innovation
    [projectExplorationTagKeys.techAndInnovationForYou]: buildCategorySearch(projectCatalogCategoriesTechAndInnovation),
    [projectExplorationTagKeys.techAndInnovationUpcoming]: buildCategorySearch(projectCatalogCategoriesTechAndInnovation, commonPhaseTypes.upcoming),
    [projectExplorationTagKeys.techAndInnovationCrowdfunding]: buildCategorySearch(projectCatalogCategoriesTechAndInnovation, commonPhaseTypes.crowdfunding),
    [projectExplorationTagKeys.techAndInnovationLatePledge]: buildCategorySearch(projectCatalogCategoriesTechAndInnovation, commonPhaseTypes.latePledge),
    [projectExplorationTagKeys.techAndInnovationEndingSoon]: buildCategorySearch(projectCatalogCategoriesTechAndInnovation, commonPhaseTypes.active, commonSortTypes.endingSoon),

    // Creative Works
    [projectExplorationTagKeys.creativeWorksForYou]: buildCategorySearch(projectCatalogCategoriesCreativeWorks),
    [projectExplorationTagKeys.creativeWorksUpcoming]: buildCategorySearch(projectCatalogCategoriesCreativeWorks, commonPhaseTypes.upcoming),
    [projectExplorationTagKeys.creativeWorksCrowdfunding]: buildCategorySearch(projectCatalogCategoriesCreativeWorks, commonPhaseTypes.crowdfunding),
    [projectExplorationTagKeys.creativeWorksLatePledge]: buildCategorySearch(projectCatalogCategoriesCreativeWorks, commonPhaseTypes.latePledge),
    [projectExplorationTagKeys.creativeWorksEndingSoon]: buildCategorySearch(projectCatalogCategoriesCreativeWorks, commonPhaseTypes.active, commonSortTypes.endingSoon),

    // Community Projects
    [projectExplorationTagKeys.communityProjectsForYou]: buildCategorySearch(projectCatalogCategoriesCommunityProjects, commonPhaseTypes.active),
    [projectExplorationTagKeys.communityProjectsUpcoming]: buildCategorySearch(projectCatalogCategoriesCommunityProjects, commonPhaseTypes.upcoming),
    [projectExplorationTagKeys.communityProjectsCrowdfunding]: buildCategorySearch(projectCatalogCategoriesCommunityProjects, commonPhaseTypes.crowdfunding),
    [projectExplorationTagKeys.communityProjectsLatePledge]: buildCategorySearch(projectCatalogCategoriesCommunityProjects, commonPhaseTypes.latePledge),
    [projectExplorationTagKeys.communityProjectsEndingSoon]: buildCategorySearch(projectCatalogCategoriesCommunityProjects, null, commonSortTypes.endingSoon),

    // Gaming Culture
    [projectExplorationTagKeys.gamingCultureBoardAndCard]: buildCategorySearch([projectCatalogCategories[projectCatalogCategories.boardAndCardGames].name]),
    [projectExplorationTagKeys.gamingCultureTTRPG]: buildCategorySearch([projectCatalogCategories[projectCatalogCategories.ttrpg].name]),
    [projectExplorationTagKeys.gamingCultureVideo]: buildCategorySearch([projectCatalogCategories[projectCatalogCategories.videoGames].name]),
    [projectExplorationTagKeys.gamingCultureOther]: buildCategorySearch([projectCatalogCategories[projectCatalogCategories.others].name]),

    // New On The Platform
    [projectExplorationTagKeys.newOnThePlatformUpcoming]: buildPhaseSearch(commonPhaseTypes.upcoming, commonSortTypes.newest),
    [projectExplorationTagKeys.newOnThePlatformCrowdfunding]: buildPhaseSearch(commonPhaseTypes.crowdfunding, commonSortTypes.newest),
    [projectExplorationTagKeys.newOnThePlatformLatePledge]: buildPhaseSearch(commonPhaseTypes.latePledge, commonSortTypes.newest),
};

export const featuredProjectSearchParamsValues = {
    [projectCatalogCategories.techAndInnovation]: buildCategorySearch(projectCatalogCategoriesTechAndInnovation),
    [projectCatalogCategories.creativeWorks]: buildCategorySearch(projectCatalogCategoriesCreativeWorks),
    [projectCatalogCategories.communityProjects]: buildCategorySearch(projectCatalogCategoriesCommunityProjects),
    [projectCatalogCategories.gamingCulture]: buildCategorySearch(projectCatalogCategoriesGamingCulture),
};
